import React from 'react';
import logoDark from '../../assets/logo/bookbin_logo_dark.svg';
import logoLight from '../../assets/logo/bookbin_logo_light.svg';
import logoIcon from '../../assets/logo/bookbin_Icon.svg';

interface LogoProps {
  /**
   * Logo variant:
   * - 'light': Use light logo (dark text, for light backgrounds)
   * - 'dark': Use dark logo (light text, for dark backgrounds)
   * - 'icon': Use icon only (no text)
   */
  variant?: 'light' | 'dark' | 'icon';

  /**
   * Size preset or custom className
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Custom className for additional styling
   */
  className?: string;

  /**
   * Alt text for accessibility
   */
  alt?: string;

  /**
   * Custom logo URL (overrides default full logo if provided)
   * Used for white labeling with tenant-specific branding
   */
  customLogoUrl?: string | null;

  /**
   * Custom icon URL (overrides default icon logo if provided)
   * Used for white labeling with tenant-specific branding
   */
  customIconUrl?: string | null;
}

const sizeClasses = {
  xs: 'h-6',
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12',
  xl: 'h-16',
};

/**
 * Logo component for LearnLeaf application
 *
 * Automatically switches between light/dark variants based on theme.
 * Mobile-first design with responsive sizing.
 *
 * @example
 * // Light logo for dark header background
 * <Logo variant="dark" size="md" />
 *
 * @example
 * // Dark logo for light background
 * <Logo variant="light" size="lg" />
 *
 * @example
 * // Icon only for mobile nav
 * <Logo variant="icon" size="sm" />
 */
export const Logo: React.FC<LogoProps> = ({
  variant = 'light',
  size = 'md',
  className = '',
  alt = 'LearnLeaf',
  customLogoUrl = null,
  customIconUrl = null
}) => {
  // Determine logo source based on variant and custom URLs
  let logoSrc: string;

  if (variant === 'icon') {
    // Use custom icon URL if provided, otherwise use default icon
    logoSrc = customIconUrl || logoIcon;
  } else {
    // Use custom logo URL if provided, otherwise use default light/dark logo
    logoSrc = customLogoUrl || (variant === 'dark' ? logoDark : logoLight);
  }

  const sizeClass = sizeClasses[size];

  return (
    <img
      src={logoSrc}
      alt={alt}
      className={`${sizeClass} w-auto object-contain ${className}`}
      style={{ display: 'block', maxHeight: '100%' }}
    />
  );
};

export default Logo;
